import { PlusIcon } from "lucide-react";
import { getStatusConfig } from "~/components/command-center/statusConfig";
import { groupBoardTasksByStatus } from "~/components/projects/boardColumns";
import { cn } from "~/lib/utils";
import { ProjectTaskRow } from "./ProjectTaskRow";
import {
  EMPTY_PROJECT_MILESTONES,
  EMPTY_TASK_CONVERSATION_IDS,
  type ProjectTaskViewProps,
} from "./taskViewTypes";
import { taskMilestoneGroups, tasksByMilestoneGroup } from "./taskMilestoneGroups";
import { workflowGroupForTask, workflowGroupsForTasks } from "./taskWorkflowGroups";

/**
 * Status-grouped card list — the tasks view on narrow viewports, where a wide
 * column table would force horizontal scrolling. Desktop uses the DataTable.
 *
 * Like the desktop DataTable (and Linear's list view), only populated status
 * groups render here — empty status columns belong on the board, not the list,
 * so both breakpoints stay consistent.
 */
export function ProjectTaskMobileList({
  tasks,
  threadIndex,
  pendingTaskIds,
  conversationTaskIds = EMPTY_TASK_CONVERSATION_IDS,
  onOpenTask,
  onOpenThread,
  workItemPrefix,
  groupBy = "status",
  milestones = EMPTY_PROJECT_MILESTONES,
  workflow = null,
  backlogView = false,
  onCreateInLane,
  mutationDisabled = false,
}: ProjectTaskViewProps) {
  if (groupBy === "milestone") {
    return (
      <MilestoneGroupedMobileTasks
        tasks={tasks}
        milestones={milestones}
        threadIndex={threadIndex}
        pendingTaskIds={pendingTaskIds}
        conversationTaskIds={conversationTaskIds}
        onOpenTask={onOpenTask}
        onOpenThread={onOpenThread}
        workItemPrefix={workItemPrefix}
        backlogView={backlogView}
        onCreateInLane={onCreateInLane}
        mutationDisabled={mutationDisabled}
        workflow={workflow}
      />
    );
  }
  // ProjectTaskListView already supplies the authoritative row order. Preserve
  // it here so a user-selected sort has identical desktop and phone behavior.
  const activeTasks = tasks.filter((task) => !task.archived);
  const columns = workflow
    ? workflowGroupsForTasks(activeTasks, workflow).map((group) => ({
        status: group.key,
        label: group.label,
        leading: group.leading,
        tasks: activeTasks.filter((task) => workflowGroupForTask(task, workflow).key === group.key),
      }))
    : groupBoardTasksByStatus(activeTasks)
        .filter((column) => column.tasks.length > 0)
        .map((column) => Object.assign({}, column, { leading: null }));

  if (columns.length === 0) {
    return <p className="p-4 text-center text-sm text-muted-foreground">No tasks yet.</p>;
  }

  return (
    <div className="flex flex-col gap-3 p-3">
      {columns.map((column) => {
        const config = getStatusConfig(column.status);
        return (
          <section key={column.status}>
            <div className="mb-1 flex items-center gap-1.5 rounded-md bg-muted px-2 py-1">
              {column.leading ?? (
                <span
                  className={cn(
                    "inline-flex size-3 shrink-0 rounded-full border-2",
                    config.circleClass,
                  )}
                  aria-hidden="true"
                />
              )}
              <h3 className="text-sm font-medium text-foreground/80">{column.label}</h3>
              <span className="tabular-nums text-sm text-muted-foreground/60">
                {column.tasks.length}
              </span>
            </div>
            <div className="overflow-hidden rounded-md shadow-[inset_0_0_0_1px_hsl(var(--border)/0.55)]">
              {column.tasks.map((task) => (
                <ProjectTaskRow
                  key={task.id}
                  task={task}
                  threadIndex={threadIndex}
                  pending={pendingTaskIds.has(task.id)}
                  hasConversation={conversationTaskIds.has(task.id)}
                  onOpenTask={onOpenTask}
                  onOpenThread={onOpenThread}
                  workItemPrefix={workItemPrefix}
                />
              ))}
            </div>
          </section>
        );
      })}
    </div>
  );
}

type MilestoneGroupedMobileTasksProps = Pick<
  ProjectTaskViewProps,
  | "tasks"
  | "milestones"
  | "threadIndex"
  | "pendingTaskIds"
  | "conversationTaskIds"
  | "onOpenTask"
  | "onOpenThread"
  | "workItemPrefix"
  | "backlogView"
  | "onCreateInLane"
  | "mutationDisabled"
  | "workflow"
>;

function MilestoneGroupedMobileTasks(props: MilestoneGroupedMobileTasksProps) {
  const active = props.tasks.filter((task) => !task.archived);
  const milestoneGroups = taskMilestoneGroups(props.milestones ?? []);
  const groupedTasks = tasksByMilestoneGroup(active, milestoneGroups);
  const groups = milestoneGroups
    .map((group) => Object.assign({}, group, { tasks: groupedTasks.get(group.key) ?? [] }))
    .filter((group) => props.backlogView === true || group.tasks.length > 0);
  const canCreateBacklogTask =
    props.backlogView === true && props.onCreateInLane != null && props.mutationDisabled !== true;
  if (groups.length === 0) {
    return <p className="p-4 text-center text-sm text-muted-foreground">No tasks yet.</p>;
  }
  return (
    <div className="flex flex-col gap-3 p-3">
      {groups.map((group) => (
        <section key={group.key}>
          <div className="mb-1 flex min-h-touch items-center gap-1.5 rounded-md bg-muted px-2 py-1">
            <h3 className="text-sm font-medium text-foreground/80">{group.label}</h3>
            <span className="tabular-nums text-sm text-muted-foreground/60">
              {group.tasks.length}
            </span>
            {canCreateBacklogTask ? (
              <button
                type="button"
                className="ml-auto inline-flex size-touch items-center justify-center rounded-md text-muted-foreground hover:bg-background/60 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                aria-label={`Add task in ${group.label}`}
                onClick={() => {
                  const backlogStage = props.workflow?.stages.find(
                    (stage) => stage.category === "backlog",
                  );
                  props.onCreateInLane?.({
                    status: "backlog",
                    milestoneId: group.milestoneId,
                    ...(backlogStage != null ? { workflowStageId: backlogStage.id } : {}),
                  });
                }}
              >
                <PlusIcon className="size-4" aria-hidden="true" />
              </button>
            ) : null}
          </div>
          <div className="overflow-hidden rounded-md shadow-[inset_0_0_0_1px_hsl(var(--border)/0.55)]">
            {group.tasks.map((task) => (
              <ProjectTaskRow
                key={task.id}
                task={task}
                threadIndex={props.threadIndex}
                pending={props.pendingTaskIds.has(task.id)}
                hasConversation={(props.conversationTaskIds ?? EMPTY_TASK_CONVERSATION_IDS).has(
                  task.id,
                )}
                onOpenTask={props.onOpenTask}
                onOpenThread={props.onOpenThread}
                workItemPrefix={props.workItemPrefix}
              />
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}
